/**
 * Blog plan + credit ledger (Stripe add-on).
 * Persisted under `template_settings.tweaks.blogCredits`.
 * Entitlement is detected from `user.stripe_detail` (same pattern as Website).
 */

import { StaticVariables } from "../StaticVariables";
import {
  BLOG_LIVE_PRODUCT_IDS,
  getBlogPriceId,
  getBlogProductId,
  getBlogProductIds,
  isStripeTestModeSync,
  type BlogStripePlan,
} from "../StripeTestMode";

export type BlogPlanKey = BlogStripePlan;

export const BLOG_PLAN_ALLOWANCES: Record<
  BlogPlanKey,
  {
    postsPerMonth: number;
    generationCredits: number;
    publishCredits: number;
    label: string;
    priceLabel: string;
    description: string;
  }
> = {
  blog_4: {
    postsPerMonth: 4,
    generationCredits: 16,
    publishCredits: 4,
    label: "4 posts / month",
    priceLabel: "Subscribe",
    description:
      "16 generation credits and 4 publishing credits each month. Weekly roundup uses 1 generation credit per post.",
  },
  blog_12: {
    postsPerMonth: 12,
    generationCredits: 48,
    publishCredits: 12,
    label: "12 posts / month",
    priceLabel: "Subscribe",
    description:
      "48 generation credits and 12 publishing credits each month. Every-other-day cadence uses about 3 generation credits per week.",
  },
};

export type BlogCreditsState = {
  plan: BlogPlanKey | null;
  generationRemaining: number;
  publishRemaining: number;
  /** ISO date (UTC) for the start of the current credit period. */
  periodStart: string | null;
  lastSyncedAt?: string | null;
};

export const DEFAULT_BLOG_CREDITS: BlogCreditsState = {
  plan: null,
  generationRemaining: 0,
  publishRemaining: 0,
  periodStart: null,
  lastSyncedAt: null,
};

export function isBlogPlanKey(value: unknown): value is BlogPlanKey {
  return value === "blog_4" || value === "blog_12";
}

function parseStripeDetail(raw: unknown): any | null {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return null;
}

function activeStripeSubs(userData: any): any[] {
  const parsed = parseStripeDetail(userData?.stripe_detail);
  if (!parsed) return [];
  const list = Array.isArray(parsed.subscriptions)
    ? parsed.subscriptions
    : parsed.subscription
      ? [parsed.subscription]
      : [];
  return list.filter((sub) => {
    const status = String(sub?.subscription_status || sub?.status || "").toLowerCase();
    const plan = String(sub?.plan || "").toLowerCase();
    return !status.includes("cancel") && plan !== "cancelled";
  });
}

function normalizeProductToken(value: unknown): string {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");
}

/** Match Stripe line items / plan names / product ids to a Blog plan. */
export function blogPlanKeyFromStripeRef(value: unknown): BlogPlanKey | null {
  const raw = String(value || "").trim();
  if (!raw) return null;
  if (isBlogPlanKey(raw)) return raw;

  const ids = getBlogProductIds();
  if (raw === ids.blog_4 || raw === BLOG_LIVE_PRODUCT_IDS.blog_4) return "blog_4";
  if (raw === ids.blog_12 || raw === BLOG_LIVE_PRODUCT_IDS.blog_12) {
    return "blog_12";
  }

  const token = normalizeProductToken(raw);
  if (
    /\bblog\b/.test(token) &&
    (/\b12\b/.test(token) || token.includes("twelve"))
  ) {
    return "blog_12";
  }
  if (
    /\bblog\b/.test(token) &&
    (/\b4\b/.test(token) || token.includes("four"))
  ) {
    return "blog_4";
  }
  return null;
}

/**
 * Highest Blog add-on on an active Stripe subscription.
 * Prefers 12/mo when both somehow appear.
 */
export function resolveBlogPlanFromStripe(userData: any): BlogPlanKey | null {
  let found: BlogPlanKey | null = null;
  for (const sub of activeStripeSubs(userData)) {
    const candidates: unknown[] = [
      sub?.plan,
      sub?.product_id,
      sub?.metadata?.planName,
      sub?.metadata?.blog_plan,
    ];
    const items = Array.isArray(sub?.items) ? sub.items : [];
    for (const item of items) {
      candidates.push(item?.name, item?.product_id, item?.price_id, item?.plan);
    }
    for (const c of candidates) {
      const key = blogPlanKeyFromStripeRef(c);
      if (!key) continue;
      if (key === "blog_12") return "blog_12";
      found = found || key;
    }
  }
  return found;
}

export function normalizeBlogCredits(raw: unknown): BlogCreditsState {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...DEFAULT_BLOG_CREDITS };
  }
  const o = raw as Record<string, unknown>;
  const plan = isBlogPlanKey(o.plan) ? o.plan : null;
  const gen = Number(o.generationRemaining);
  const pub = Number(o.publishRemaining);
  return {
    plan,
    generationRemaining: Number.isFinite(gen) ? Math.max(0, Math.floor(gen)) : 0,
    publishRemaining: Number.isFinite(pub) ? Math.max(0, Math.floor(pub)) : 0,
    periodStart:
      typeof o.periodStart === "string" && o.periodStart.trim()
        ? o.periodStart
        : null,
    lastSyncedAt:
      typeof o.lastSyncedAt === "string" && o.lastSyncedAt.trim()
        ? o.lastSyncedAt
        : null,
  };
}

function utcMonthStartIso(d = new Date()): string {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString();
}

function isSameUtcMonth(iso: string | null | undefined, d = new Date()): boolean {
  if (!iso) return false;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  const then = new Date(t);
  return (
    then.getUTCFullYear() === d.getUTCFullYear() &&
    then.getUTCMonth() === d.getUTCMonth()
  );
}

/**
 * Align persisted credits with Stripe entitlement.
 * Resets allowances when the plan changes or the UTC month rolls over.
 */
export function syncBlogCreditsWithStripe(
  stored: unknown,
  userData: any,
): BlogCreditsState {
  const current = normalizeBlogCredits(stored);
  const stripePlan = resolveBlogPlanFromStripe(userData);
  const plan = stripePlan || current.plan;
  if (!plan) {
    return { ...DEFAULT_BLOG_CREDITS };
  }
  const allowance = BLOG_PLAN_ALLOWANCES[plan];
  const needsReset =
    current.plan !== plan ||
    !isSameUtcMonth(current.periodStart) ||
    current.periodStart == null;
  if (!needsReset) {
    return {
      ...current,
      plan,
      lastSyncedAt: new Date().toISOString(),
    };
  }
  return {
    plan,
    generationRemaining: allowance.generationCredits,
    publishRemaining: allowance.publishCredits,
    periodStart: utcMonthStartIso(),
    lastSyncedAt: new Date().toISOString(),
  };
}

/**
 * True when the creator has an active Blog Stripe plan (or synced credits).
 * `blog_feature` only gates sidebar visibility — not generate/publish.
 */
export function isBlogCreditsEnforced(
  credits: BlogCreditsState,
  userData?: any,
): boolean {
  return Boolean(credits.plan || resolveBlogPlanFromStripe(userData));
}

export function hasActiveBlogPlan(
  credits: BlogCreditsState,
  userData?: any,
): boolean {
  return isBlogCreditsEnforced(credits, userData);
}

/**
 * Admin always. Creators need an active Blog plan + a publish credit.
 */
export function isBlogPublishingAllowed(opts: {
  isAdmin?: boolean;
  credits: BlogCreditsState;
  userData?: any;
}): boolean {
  if (opts.isAdmin) return true;
  if (!hasActiveBlogPlan(opts.credits, opts.userData)) return false;
  return opts.credits.publishRemaining > 0;
}

/** Admin always. Creators need an active Blog plan to generate (regen is free with a plan). */
export function isBlogGenerationAllowed(opts: {
  isAdmin?: boolean;
  credits: BlogCreditsState;
  userData?: any;
}): boolean {
  if (opts.isAdmin) return true;
  return hasActiveBlogPlan(opts.credits, opts.userData);
}

export type BlogGenerationSpendKind =
  | "manual"
  | "weekly_roundup"
  | "ai_best_post"
  | "regenerate";

/** Credits to spend for one generation action. Regenerate is always free. */
export function generationCreditCost(kind: BlogGenerationSpendKind): number {
  if (kind === "regenerate") return 0;
  if (kind === "weekly_roundup") return 1;
  if (kind === "ai_best_post") return 1;
  return 1;
}

/** Soft check when enabling cadence (weekly ≈ 1/wk, every-other-day ≈ 3/wk). */
export function cadenceWeeklyGenerationBudget(
  cadence: "off" | "weekly" | "aiBest",
): number {
  if (cadence === "weekly") return 1;
  if (cadence === "aiBest") return 3;
  return 0;
}

export function canSpendGenerationCredits(
  credits: BlogCreditsState,
  cost: number,
  opts?: { isAdmin?: boolean; userData?: any },
): { ok: boolean; message?: string } {
  if (opts?.isAdmin) return { ok: true };
  if (!hasActiveBlogPlan(credits, opts?.userData)) {
    return {
      ok: false,
      message: "Subscribe to a Blog plan to generate posts.",
    };
  }
  // Regenerate (cost 0) is free once they have a plan.
  if (cost <= 0) return { ok: true };
  if (credits.generationRemaining >= cost) return { ok: true };
  return {
    ok: false,
    message:
      credits.generationRemaining <= 0
        ? "You're out of blog generation credits for this month. Upgrade your Blog plan or wait until next month."
        : `Not enough generation credits (need ${cost}, have ${credits.generationRemaining}).`,
  };
}

export function canSpendPublishCredit(
  credits: BlogCreditsState,
  opts?: { isAdmin?: boolean; userData?: any },
): { ok: boolean; message?: string } {
  if (opts?.isAdmin) return { ok: true };
  if (!hasActiveBlogPlan(credits, opts?.userData)) {
    return {
      ok: false,
      message: "Subscribe to a Blog plan to publish posts.",
    };
  }
  if (credits.publishRemaining > 0) return { ok: true };
  return {
    ok: false,
    message:
      "You're out of publishing credits for this month. Upgrade your Blog plan or wait until next month.",
  };
}

export function applyGenerationSpend(
  credits: BlogCreditsState,
  cost: number,
): BlogCreditsState {
  if (cost <= 0) return credits;
  return {
    ...credits,
    generationRemaining: Math.max(0, credits.generationRemaining - cost),
    lastSyncedAt: new Date().toISOString(),
  };
}

export function applyPublishSpend(credits: BlogCreditsState): BlogCreditsState {
  return {
    ...credits,
    publishRemaining: Math.max(0, credits.publishRemaining - 1),
    lastSyncedAt: new Date().toISOString(),
  };
}

/**
 * Start Stripe Checkout for a Blog add-on.
 * Uses the same `/api/stripe/create-checkout-session` shape as LiB plans.
 */
export async function createBlogCheckoutSession(input: {
  plan: BlogPlanKey;
  userId?: string;
  email?: string;
  successPath?: string;
  cancelPath?: string;
}): Promise<{ url?: string; error?: string }> {
  const API_URL = StaticVariables().API_BASE;
  const testMode = isStripeTestModeSync();
  // Backend appends session_id={CHECKOUT_SESSION_ID} — do not pre-add it.
  const successPath =
    input.successPath || "/dashboard/blog/settings?blog_checkout=1";
  const cancelPath = input.cancelPath || "/dashboard/blog/settings";
  try {
    const productId = getBlogProductId(input.plan);
    const priceId = getBlogPriceId(input.plan).trim();
    // Always send productId (live Blog products). Avoid LiB payment-link path —
    // Blog has no payment links and $0 prices resolve from the product on the API.
    const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        productId,
        ...(priceId ? { priceId } : {}),
        planName: input.plan,
        blogPlan: input.plan,
        userId: input.userId,
        email: input.email,
        successUrl: `${window.location.origin}${successPath}`,
        cancelUrl: `${window.location.origin}${cancelPath}`,
        testMode,
        tolt_referral:
          typeof window !== "undefined" ? (window as any).tolt_referral : undefined,
      }),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => "");
      return {
        error:
          text?.slice(0, 180) ||
          "Couldn't start Blog checkout. Confirm Blog Stripe prices are configured on the API.",
      };
    }
    const data = (await response.json().catch(() => ({}))) as { url?: string };
    if (!data?.url) return { error: "No checkout URL received from Stripe." };
    return { url: data.url };
  } catch (e) {
    console.warn("[blogCredits] checkout failed", e);
    return { error: "Couldn't reach the billing service." };
  }
}

/** Grant credits after a verified Blog checkout (optimistic until webhook syncs stripe_detail). */
export function creditsAfterBlogCheckout(
  plan: BlogPlanKey,
  previous?: BlogCreditsState | null,
): BlogCreditsState {
  const allowance = BLOG_PLAN_ALLOWANCES[plan];
  const prev = previous || DEFAULT_BLOG_CREDITS;
  // Same-month upgrade: keep remaining but bump ceilings by delta when moving 4→12.
  if (
    prev.plan &&
    prev.plan !== plan &&
    isSameUtcMonth(prev.periodStart)
  ) {
    const prevA = BLOG_PLAN_ALLOWANCES[prev.plan];
    const genDelta = allowance.generationCredits - prevA.generationCredits;
    const pubDelta = allowance.publishCredits - prevA.publishCredits;
    return {
      plan,
      generationRemaining: Math.max(
        0,
        prev.generationRemaining + Math.max(0, genDelta),
      ),
      publishRemaining: Math.max(
        0,
        prev.publishRemaining + Math.max(0, pubDelta),
      ),
      periodStart: prev.periodStart || utcMonthStartIso(),
      lastSyncedAt: new Date().toISOString(),
    };
  }
  return {
    plan,
    generationRemaining: allowance.generationCredits,
    publishRemaining: allowance.publishCredits,
    periodStart: utcMonthStartIso(),
    lastSyncedAt: new Date().toISOString(),
  };
}

export function blogPlanKeyFromCheckoutSession(session: any): BlogPlanKey | null {
  if (!session) return null;
  return (
    blogPlanKeyFromStripeRef(session?.metadata?.blog_plan) ||
    blogPlanKeyFromStripeRef(session?.metadata?.planName) ||
    blogPlanKeyFromStripeRef(session?.planName) ||
    blogPlanKeyFromStripeRef(session?.plan_details?.name) ||
    // Expanded subscription from verify-checkout-session
    blogPlanKeyFromStripeRef(session?.subscription?.metadata?.blog_plan) ||
    blogPlanKeyFromStripeRef(session?.subscription?.metadata?.planName) ||
    blogPlanKeyFromStripeRef(session?.subscription?.items?.data?.[0]?.price?.product) ||
    null
  );
}
